import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Door, FloppyDisk, Gauge, Hash, MapPin, Ruler } from '@phosphor-icons/react';
import { DialogHuelle, FeldRaster, ImmoButton, SearchSelect, TextFeld, type SucheOption } from '../../../components/immobilien/ui';
import { supabase } from '../../../lib/supabase/client';
import { bereinigeSuchbegriff } from '../../../lib/kontakte/personenSuche';
import { hausAdresse, sortiereHaeuser, type Haus } from '../../../lib/objekte/haeuser';
import { zaehlerPayload } from '../../../lib/immobilien/zaehlerFormular';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { EIGENER_TYP, entwurfAus, formularFehler, mitTyp, nummerMitPraefix, objektLabel, TYP_AUSWAHL, type ZaehlerEntwurf } from '../../../lib/zaehler/formular';
import type { ZaehlerDaten } from '../../../lib/zaehler/useZaehlerDetail';
import { FehlerBanner, FeldBlock, FeldSelect, NotizFeld } from './teile';

interface ObjektTreffer { id: string; bezeichnung: string | null; strasse: string | null; hausnummer: string | null; objektnummer: string | null }
interface MieterTreffer { id: string; vorname: string | null; nachname: string | null; email: string | null }

const muster = (anfrage: string) => `%${bereinigeSuchbegriff(anfrage).replace(/%/g, ' ')}%`;
const mieterName = (m: { vorname: string | null; nachname: string | null }) => [m.vorname, m.nachname].filter(Boolean).join(' ');

/**
 * „Neuer Zähler“ / „Zähler bearbeiten“ (Handoff Zähler §5.4) – Felder, Typ-Vorbelegung, Objektnummer-Präfix,
 * Haus-Auswahl und Payload wie das bisherige Formular. Esc und Klick daneben schließen.
 */
export default function ZaehlerFormular({ zaehler, onSchliessen, onGespeichert }: {
  /** null = neuer Zähler */
  zaehler: ZaehlerDaten | null;
  onSchliessen: () => void;
  onGespeichert: (meldung: string) => void;
}) {
  const id = useId();
  const { mobil } = useMobilModus();
  const [e, setE] = useState<ZaehlerEntwurf>(() => entwurfAus(zaehler));
  const [objekt, setObjekt] = useState<(SucheOption & { objektnummer: string | null }) | null>(() =>
    zaehler?.objekt ? { id: zaehler.objekt.id, label: objektLabel(zaehler.objekt), objektnummer: zaehler.objekt.objektnummer } : null);
  const [mieter, setMieter] = useState<SucheOption | null>(() =>
    zaehler?.mieter ? { id: zaehler.mieter.id, label: mieterName(zaehler.mieter) || '—' } : null);
  const [haeuser, setHaeuser] = useState<Haus[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  // zuletzt vorgeschlagener Objektnummer-Präfix – nur der wird beim Objektwechsel ersetzt
  const vorschlag = useRef('');
  const set = (f: keyof ZaehlerEntwurf) => (v: string) => setE(x => ({ ...x, [f]: v }));

  // Häuser des gewählten Objekts (leer = kein Feld)
  useEffect(() => {
    if (!objekt) { setHaeuser([]); return; }
    let aktiv = true;
    void supabase.from('objekt_haeuser').select('*').eq('objekt_id', objekt.id).then(({ data }) => {
      if (aktiv) setHaeuser(sortiereHaeuser((data as Haus[] | null) ?? []));
    });
    return () => { aktiv = false; };
  }, [objekt]);

  const sucheObjekte = useCallback(async (anfrage: string, _t: string | null, signal: AbortSignal): Promise<SucheOption[]> => {
    const m = muster(anfrage);
    const { data, error } = await supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer, objektnummer')
      .or(`bezeichnung.ilike.${m},strasse.ilike.${m},objektnummer.ilike.${m}`).limit(8).abortSignal(signal);
    if (error) throw new Error(error.message);
    return ((data ?? []) as ObjektTreffer[]).map(o => ({
      id: o.id, label: `${o.objektnummer ? `${o.objektnummer} · ` : ''}${o.bezeichnung ?? ''}`,
      sub: [o.strasse, o.hausnummer].filter(Boolean).join(' ') || undefined, objektnummer: o.objektnummer,
    }));
  }, []);

  const sucheMieter = useCallback(async (anfrage: string, _t: string | null, signal: AbortSignal): Promise<SucheOption[]> => {
    const m = muster(anfrage);
    const { data, error } = await supabase.from('mieter').select('id, vorname, nachname, email')
      .or(`vorname.ilike.${m},nachname.ilike.${m},email.ilike.${m}`).limit(8).abortSignal(signal);
    if (error) throw new Error(error.message);
    return ((data ?? []) as MieterTreffer[]).map(x => ({ id: x.id, label: mieterName(x) || '—', sub: x.email ?? undefined }));
  }, []);

  const objektWaehlen = (o: SucheOption) => {
    const nr = (o as SucheOption & { objektnummer?: string | null }).objektnummer ?? null;
    const n = nummerMitPraefix(e.zaehler_nummer, vorschlag.current, nr);
    vorschlag.current = n.vorschlag;
    setE(x => ({ ...x, zaehler_nummer: n.nummer, haus_id: '' }));
    setObjekt({ id: o.id, label: o.sub ? `${o.label} — ${o.sub}` : o.label, objektnummer: nr });
  };

  const speichere = async () => {
    const f = formularFehler(e, objekt?.id ?? null);
    if (f) { setFehler(f); return; }
    setFehler(null);
    setLaeuft(true);
    const payload = zaehlerPayload({ ...e, mieter_id: mieter?.id ?? '' }, objekt!.id, haeuser.map(h => h.id), new Date().toISOString());
    const r = zaehler ? await supabase.from('zaehler').update(payload).eq('id', zaehler.id) : await supabase.from('zaehler').insert(payload);
    setLaeuft(false);
    if (r.error) { setFehler(`Fehler beim Speichern: ${r.error.message || 'Unbekannter Fehler'}`); return; }
    onGespeichert(zaehler ? 'Zähler gespeichert' : 'Zähler erstellt');
  };

  const breiteKnopf: React.CSSProperties = { flex: 1, height: 48 };
  return (
    <DialogHuelle titel={zaehler ? 'Zähler bearbeiten' : 'Neuer Zähler'} untertitel="Stammdaten" icon={Gauge} breite={620} vollbild={mobil}
      onSchliessen={onSchliessen} laeuft={laeuft}
      fuss={(
        <>
          <ImmoButton art="akzent" groesse={48} pille={false} icon={FloppyDisk} onClick={() => { void speichere(); }} disabled={laeuft} style={breiteKnopf}>
            {zaehler ? 'Speichern' : 'Erstellen'}
          </ImmoButton>
          <ImmoButton groesse={48} pille={false} onClick={onSchliessen} disabled={laeuft} style={{ fontSize: 14 }}>Abbrechen</ImmoButton>
        </>
      )}>
      <div className="flex flex-col" style={{ gap: 14 }}>
        {fehler && <FehlerBanner text={fehler} />}
        <TextFeld label="Zählernummer *" icon={Hash} wert={e.zaehler_nummer} onChange={set('zaehler_nummer')} placeholder="z.B. 1ESY1234567890" autoFocus={!zaehler} />
        <FeldRaster minBreite={200}>
          <FeldBlock label="Typ *" htmlFor={`${id}-typ`}>
            <FeldSelect id={`${id}-typ`} hoehe={50} wert={e.typ} onChange={v => setE(x => mitTyp(x, v))}>
              {TYP_AUSWAHL.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </FeldSelect>
            {e.typ === EIGENER_TYP && (
              <input value={e.eigenerTyp} onChange={ev => set('eigenerTyp')(ev.target.value)} placeholder="Typ eingeben..." aria-label="Typ eingeben" autoFocus
                style={{ height: 50, padding: '0 14px', borderRadius: 15, border: 'none', outline: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', marginTop: 4 }} />
            )}
          </FeldBlock>
          <TextFeld label="Messeinheit" icon={Ruler} wert={e.einheit} onChange={set('einheit')} placeholder="kWh, m³, Liter..." />
        </FeldRaster>
        <TextFeld label="Standort" icon={MapPin} wert={e.standort} onChange={set('standort')} placeholder="z.B. Keller Raum 3" />
        <SearchSelect label="Objekt *" platzhalter="Objekt suchen..." suche={sucheObjekte} onWahl={objektWaehlen}
          ausgewaehlt={objekt} onEntfernen={() => { setObjekt(null); setE(x => ({ ...x, haus_id: '' })); }} />
        {haeuser.length > 0 && (
          <FeldBlock label="Haus" htmlFor={`${id}-haus`}>
            <FeldSelect id={`${id}-haus`} hoehe={50} wert={e.haus_id} onChange={set('haus_id')}>
              <option value="">Ohne Haus (ganze Liegenschaft)</option>
              {haeuser.map(h => <option key={h.id} value={h.id}>{hausAdresse(h) ? `${h.bezeichnung} · ${hausAdresse(h)}` : h.bezeichnung}</option>)}
            </FeldSelect>
          </FeldBlock>
        )}
        <TextFeld label="Einheit / Wohnung" icon={Door} wert={e.einheit_bezeichnung} onChange={set('einheit_bezeichnung')} placeholder="z.B. Whg 04, Gewerbe EG" />
        <div className="flex flex-col" style={{ gap: 6 }}>
          <SearchSelect label="Mieter (optional)" platzhalter="Mieter suchen..." suche={sucheMieter} onWahl={o => setMieter(o)} ausgewaehlt={mieter} />
          {mieter && (
            <button type="button" onClick={() => setMieter(null)} className="self-start cursor-pointer"
              style={{ minHeight: mobil ? 44 : 28, padding: '0 4px', border: 'none', background: 'transparent', color: 'var(--bad)', fontSize: 12, fontWeight: 800, fontFamily: 'inherit' }}>
              Mieter entfernen
            </button>
          )}
        </div>
        <FeldBlock label="Notizen" htmlFor={`${id}-notizen`}>
          <NotizFeld id={`${id}-notizen`} wert={e.notizen} onChange={set('notizen')} />
        </FeldBlock>
      </div>
    </DialogHuelle>
  );
}
